import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { CountryCodes, strings } from '@constants'
import { getAllCommodityApi, getPanByCompanyName } from '@data'
import { createOrganisation } from '@data/conn/post/organisation'
import OrgRole from '@data/model/NewModals/OrgRole'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer } from '@data/state/action/root'
import { getNumberWithCountryCode } from '@utils'
import { DrawerBodyWrapper, DrawerFooterWrapper, TOAST_TYPE } from '@base'
import { Button, Input, MultiSelect, Select } from '@baseV2'

import { InputsWrapper, SearchRow, PhoneRow } from '../styles'

const PAN_REGEX = /^([a-zA-Z]){5}([0-9]){4}([a-zA-Z]){1}?$/

const ORG_ROLES = {
	marketplaceBuyer: { role: 'BUYER', platform: 'MARKETPLACE', name: 'CUSTOMER' },
	marketplaceSupplier: { role: 'SUPPLIER', platform: 'MARKETPLACE', name: 'VENDOR' },
	logisticsSupplier: { role: 'SUPPLIER', platform: 'LOGISTICS', name: 'LOGISTICS' },
}

const deriveStateFromData = (data) => {
	const _state = {
		pan: { value: '', required: false },
		user_name: { value: '', required: true },
		mobile_number: { value: '', required: true },
		commodities: { value: '', required: true },
		roles: { value: '', required: true },
	}
	if (!data) return _state
	_state.user_name.value = data.full_name
	_state.mobile_number.value = data.mobile_number?.slice(2)
	return _state
}

const AddOrganisation = ({ update, userDetails }) => {
	const dispatch = useDispatch()
	const user = useSelector((state) => state.getIn(['user', 'user']))

	const [details, setDetails] = useState(deriveStateFromData())
	const [countryCode, setCountryCode] = useState(CountryCodes[0])
	const [panByCompanyName, setPanByCompanyName] = useState([])
	const [searchedOrgName, setSearchedOrgName] = useState()
	const [commodityList, setCommodityList] = useState([])

	useEffect(() => {
		setDetails(deriveStateFromData(userDetails))
		dispatch(showLoader(true))
		getAllCommodityApi()
			.then((response) => setCommodityList(response))
			.catch((err) => console.log(err))
			.finally(() => dispatch(showLoader(false)))
	}, [userDetails])

	const availableRoles = useMemo(() => Object.values(ORG_ROLES).map((r) => ({ ...new OrgRole(r).toJS(), display_name: r.name })), [])

	const updateState = (field, key) => (value) => setDetails((state) => ({ ...state, [field]: { ...state[field], [key]: value } }))

	const userNameHandler = () => {
		const userName = details?.user_name?.value?.trim()
		if (userName === searchedOrgName || userName === '') return
		dispatch(showLoader(true))
		getPanByCompanyName(userName)
			.then((res) => {
				const _res =
					res?.length > 0
						? res.map((item) => {
								item['display_name'] = `${item.pan}, (${item.company_name}), ${item.location}`
								return item
						  })
						: []
				setPanByCompanyName(_res)
			})
			.catch((err) => {
				console.log(err)
				dispatch(showToast(true, err?.message, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				setSearchedOrgName(userName)
				dispatch(showLoader(false))
			})
	}

	const disabled = useMemo(() => {
		if (!details?.commodities?.value?.length || !details?.roles?.value?.length) return true
		return Object.values(details).some((d) => d.required && !d.value)
	}, [details])

	const addOrgHandler = () => {
		if (typeof details.pan?.value === 'object' && !PAN_REGEX.test(details.pan?.value?.pan)) {
			dispatch(showToast(true, 'Invalid pan number', { type: TOAST_TYPE.ERROR }))
			return
		}
		dispatch(showLoader(true))
		createOrganisation({
			user_name: details.user_name.value,
			country_code: countryCode.code,
			mobile: getNumberWithCountryCode(countryCode, details.mobile_number.value),
			pan: typeof details.pan?.value === 'object' ? details.pan?.value?.pan : details.pan?.value,
			userId: user?.user_id,
			roles: details?.roles?.value?.map((item) => ({ ...item, approved: true })),
			commodities: details?.commodities?.value,
		})
			.then((res) => {
				if (res?.status_code === 400) {
					dispatch(showToast(true, res?.message, { type: TOAST_TYPE.ERROR }))
					return
				}
				dispatch(showToast(true, 'Successfully added organisation', { type: TOAST_TYPE.SUCCESS }))
				update()
				dispatch(toggleDrawer(false))
			})
			.catch((err) => {
				console.log(err)
				dispatch(showToast(true, 'Error while adding organisation', { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => dispatch(showLoader(false)))
	}

	return (
		<>
			<DrawerBodyWrapper>
				<InputsWrapper>
					<SearchRow>
						<Input
							id='org-user-name'
							label={strings('user', 'name')}
							placeholder='Enter Org User Name'
							value={details?.user_name?.value}
							onChange={(e) => updateState('user_name', 'value')(e.target.value)}
						/>
						<Button type='button' variant='outline' color='neutral' size='base' onClick={userNameHandler}>
							{strings('search')}
						</Button>
					</SearchRow>

					<PhoneRow>
						<Select
							id='org-country-code'
							label={strings('code')}
							data={CountryCodes}
							displayKey='dial_code'
							primaryKey='name'
							value={countryCode}
							onChange={setCountryCode}
						/>
						<Input
							id='org-mobile'
							type='tel'
							label={strings('mobile')}
							maxLength={20}
							placeholder={strings('enter_your_mobile_num')}
							value={details?.mobile_number?.value}
							onChange={(e) => updateState('mobile_number', 'value')(e.target.value)}
						/>
					</PhoneRow>

					{panByCompanyName?.length > 0 ? (
						<Select
							id='org-pan-select'
							label={strings('pan', 'number')}
							placeholder={strings(['select', 'pan', 'number'])}
							data={panByCompanyName}
							displayKey='display_name'
							primaryKey='pan'
							value={panByCompanyName.find((item) => item?.pan === details.pan.value?.pan) ?? (details.pan.value || {})}
							onChange={(val) => updateState('pan', 'value')(val)}
						/>
					) : (
						<Input
							id='org-pan'
							label={strings('pan', 'number')}
							placeholder={strings('enter_valid_pan')}
							maxLength={10}
							value={details?.pan?.value}
							onChange={(e) => updateState('pan', 'value')(e.target.value.toUpperCase())}
						/>
					)}

					<MultiSelect
						id='org-commodities'
						label={strings('commodities')}
						placeholder={strings('select', 'commodities')}
						data={commodityList}
						primaryKey='commodity_id'
						displayKey='commodity_name'
						value={details?.commodities?.value === '' ? [] : details?.commodities?.value}
						onChange={updateState('commodities', 'value')}
					/>

					<MultiSelect
						id='org-roles'
						label={strings('roles')}
						placeholder={strings('select', 'roles')}
						data={availableRoles}
						primaryKey='display_name'
						displayKey='display_name'
						value={details?.roles?.value === '' ? [] : details?.roles?.value}
						onChange={updateState('roles', 'value')}
					/>
				</InputsWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<Button color='primary' disabled={disabled} onClick={addOrgHandler}>
					{strings('create', 'organisation')}
				</Button>
			</DrawerFooterWrapper>
		</>
	)
}

export default AddOrganisation
